import React, { useEffect, useRef } from "react";
import { Box } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import MultiTextTransition from "components/common/MultiTextTransition";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { extractHexColor } from "../../../../styles/hexOpacity";
import { theme } from "../../../../styles/theme";
import { sendMessage } from "../../../../utils/webview";
import useIsInappWebview from "../../../../hooks/useIsInappWebview";
import { useQuery } from "@tanstack/react-query";
import {
  ONBOARDING_DEMOGRAPHIC_LOAD_ENDPOINT,
  ONBOARDING_DOMAIN,
  ONBOARDING_PAGE_DATA_LOAD_ENDPOINT,
  onboardingApi,
} from "../../../../api/onboardingApi";
import { axiosInstance } from "../../../../api";
import { DataState } from "../../../../recoil/onboardingDataAtom";
import useMixpanelWeb from "../../../../hooks/useMixpanelWeb";

export default function CrossFunnel_Loading() {
  const navigate = useNavigate();
  const isInappWebview = useIsInappWebview();

  const user = useRecoilValue(userAtom);

  const trackMixpanel = useMixpanelWeb({
    eventName: "크로스 퍼널 로딩",
  });

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    trackMixpanel();
  });

  const { data: demographicData } = useQuery(
    [ONBOARDING_DOMAIN, ONBOARDING_DEMOGRAPHIC_LOAD_ENDPOINT, user?.userId],
    () => onboardingApi.loadDemographic(),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] &&
        !!user?.accessToken &&
        user?.isDemographicRequired === true,
      onSuccess: (data) => {
        if (data?.result) {
          sessionStorage.setItem(
            "demographic_data",
            JSON.stringify(data.result)
          );
        }
      },
    }
  );

  const { data: goal2Data } = useQuery(
    [
      ONBOARDING_DOMAIN,
      ONBOARDING_PAGE_DATA_LOAD_ENDPOINT,
      user?.userId,
      "goal_2",
    ],
    () => onboardingApi.loadPageData("goal_2"),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] &&
        !!user?.accessToken &&
        user?.isWebFunnelRegister === true,
      onSuccess: (data) => {
        if (data?.result?.data) {
          try {
            const parsedData: DataState = JSON.parse(data.result.data);
            sessionStorage.setItem("goal_2_data", JSON.stringify(parsedData));
          } catch (error) {
            console.error("Failed to parse goal_2 data:", error);
          }
        }
      },
    }
  );

  const { data: goal5Data } = useQuery(
    [
      ONBOARDING_DOMAIN,
      ONBOARDING_PAGE_DATA_LOAD_ENDPOINT,
      user?.userId,
      "goal_5",
    ],
    () => onboardingApi.loadPageData("goal_5"),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] &&
        !!user?.accessToken &&
        user?.isWebFunnelRegister === true,
      onSuccess: (data) => {
        if (data?.result?.data) {
          try {
            const parsedData: DataState = JSON.parse(data.result.data);
            sessionStorage.setItem("goal_5_data", JSON.stringify(parsedData));
          } catch (error) {
            console.error("Failed to parse goal_5 data:", error);
          }
        }
      },
    }
  );

  const handleNext = () => {
    if (user?.isDemographicRequired) {
      navigate("/onboarding_new/nickname/1");
    } else {
      navigate("/onboarding_new/result/1");
    }
  };

  const textList = [
    "이미 디스턴싱에\n가입한 적이 있으시군요.",
    "이전 정보를\n불러오겠습니다.",
  ];

  useEffect(() => {
    if (isInappWebview) {
      const color = extractHexColor(
        theme.vars.palette.Background.SoftHighlight
      );

      if (color) {
        sendMessage(
          JSON.stringify({
            event: "set_safearea_color",
            params: {
              topColor: `${color}`,
              bottomColor: `${color}`,
            },
          })
        );
      }
    }
  }, []);

  return (
    <Box
      sx={{
        // 겹치는 두 개(텍스트 / 로고)를 같은 위치에 쌓아두기
        position: "relative",
        width: "100%",
      }}
    >
      <MultiTextTransition
        textList={textList}
        fadeInTime={1000}
        displayTime={2000}
        fadeOutTime={1000}
        onComplete={handleNext}
      />
    </Box>
  );
}
